import { getAuth, onIdTokenChanged } from 'firebase/auth'
import type { Auth, User } from 'firebase/auth'
import {
  abilityRulesForRoles,
  hasPortalRole,
  parseAccountRoles,
} from '@/utils/portalRoles'
import type { Rule } from '@/plugins/casl/ability'
import { ability } from '@/plugins/casl/ability'

type Resolver = (token: string) => void
type Rejecter = (reason: Error) => void

let auth: Auth | null = null

let currentUser: User | null = null
let currentToken: string | null = null
const pendingResolvers: Array<{ resolve: Resolver; reject: Rejecter }> = []

const drain = (result: { token: string } | { error: Error }) => {
  while (pendingResolvers.length) {
    const { resolve, reject } = pendingResolvers.shift()!
    if ('token' in result)
      resolve(result.token)
    else reject(result.error)
  }
}

export const clearAuthCookies = () => {
  // Setting to null triggers Max-Age=-1 in @core/composable/useCookie, actually
  // deleting the cookie. Empty string would just store an empty value.
  useCookie('accessTokenOperi').value = null
  useCookie('userData').value = null
  useCookie('userAbilityRules').value = null
  useCookie('displayName').value = null
  useCookie('userAvatar').value = null
}

let resolveAuthReady!: () => void

// Resolves on the first onIdTokenChanged fire (logged in or not). Router guard
// awaits this so it never reads stale cookies on cold load. Always resolves;
// never rejects — otherwise navigation would hang.
export const authReady: Promise<void> = new Promise(resolve => {
  resolveAuthReady = resolve
})

/**
 * Writes the role-derived cookies the nav and the router guard read.
 *
 * Deliberately the same two helpers `login.vue` uses, so a session refreshed here and one
 * created there cannot end up describing the same account differently.
 */
const syncSessionFromClaims = (user: User, claim: unknown) => {
  const roles = parseAccountRoles(claim)

  useCookie('displayName').value = user.displayName ?? user.email
  useCookie('userAvatar').value = user.photoURL
  useCookie<Record<string, unknown>>('userData').value = {
    roles,
    fullName: user.displayName ?? user.email,
    avatar: user.photoURL,
  }

  const rules = abilityRulesForRoles(roles)

  useCookie<Rule[]>('userAbilityRules').value = rules

  // The cookie is what the router guard reads on each navigation; this is what the nav
  // renders from. Both, or the menu and the routes disagree until the next page load.
  ability.update(rules)
}

let firstFire = true
let initialized = false

// Called once from main.ts after initializeApp(firebaseConfig). The listener
// must not attach at module load — guards.ts pulls authReady from this file
// during the import phase, before main.ts has initialized Firebase.
export const initAuth = (): void => {
  if (initialized)
    return

  initialized = true
  auth = getAuth()
  currentUser = auth.currentUser

  onIdTokenChanged(auth, async user => {
    const isFirstFire = firstFire

    firstFire = false
    currentUser = user

    try {
      if (!user) {
        currentToken = null
        clearAuthCookies()
        drain({ error: new Error('User is not signed in') })

        return
      }

      // Force-refresh on the first fire to flush any cached expired token from
      // a previous tab session. Subsequent fires already mean Firebase rotated
      // the token, so a force is wasted.
      const result = await user.getIdTokenResult(isFirstFire)

      if (!hasPortalRole(result.claims.account_roles)) {
        // Customers must never hold a back-office session. signOut() re-fires this
        // listener with user=null, which clears cookies via clearAuthCookies().
        //
        // Shares hasPortalRole with login.vue on purpose: this check runs on every
        // token refresh, so when it disagreed with the login page it silently undid
        // whatever the login page had allowed.
        await auth!.signOut()
        drain({ error: new Error('User has no back-office role') })

        return
      }

      currentToken = result.token
      useCookie('accessTokenOperi').value = result.token

      // Abilities follow the token, not the login.
      //
      // They used to be written once, on the login page, so a change to what a role can
      // reach only took effect after a manual sign-out - and until then the nav was built
      // from whatever was true whenever that person last signed in. The same staleness cut
      // the other way for security: an account stripped of ADMIN in Firebase kept a cookie
      // saying `manage all`, so it went on rendering admin controls that the backend then
      // refused. Recomputing here means a refreshed token is a refreshed session.
      syncSessionFromClaims(user, result.claims.account_roles)

      drain({ token: result.token })
    }
    catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))

      drain({ error: err })
    }
    finally {
      resolveAuthReady()
    }
  })
}

export const getCurrentUser = () => currentUser

export const getCurrentToken = () => currentToken

export const waitForToken = (): Promise<string> => {
  if (currentToken)
    return Promise.resolve(currentToken)

  return new Promise<string>((resolve, reject) => {
    pendingResolvers.push({ resolve, reject })
  })
}

const goToLogin = async () => {
  // Dynamic import breaks the cycle: firebaseAuth ↔ router (which loads guards
  // that import authReady from here).
  const { router } = await import('@/plugins/1.router')

  await router.push('/login')
}

// On 401: force-refresh the ID token. Returns the fresh token on success.
// Returns null and redirects to /login if the user is gone or the refresh
// itself fails.
export const handleUnauthorized = async (): Promise<string | null> => {
  const user = currentUser
  if (!user) {
    await goToLogin()

    return null
  }

  try {
    return await user.getIdToken(true)
  }
  catch {
    try {
      await auth?.signOut()
    }
    catch {
      // signOut errors are non-actionable — listener will still fire eventually.
    }
    await goToLogin()

    return null
  }
}
